import React, { useState, useMemo } from "react";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";

import Button from "../button/Button";
import ErrorMessage from "../errorMessage/ErrorMessage";
import SingleLineTextInput from "../singleLineTextInput/SingleLineTextInput";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    [theme.breakpoints.down("md")]: {
      width: "100%",
      marginLeft: 36,
    },
    [theme.breakpoints.up("md")]: {
      width: "calc(100% - 80px)",
      marginLeft: 80,
      marginRight: 0,
    },
  },
}));

const PlayerRow = styled("div")({
  padding: "8px 0",
  display: "flex",
  alignItems: "center",
  "&:not(:last-child)": {
    borderBottom: "1px solid #ececec",
  },
});

const HeaderRow = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 10,
  paddingBottom: 10,
  borderBottom: "1px solid #ddd",
});

const SelectedCount = styled(Typography)({
  fontSize: 14,
  color: "#666",
});

function ConvertAvailabilityRequestDialog({
  availabilityRequest,
  loading,
  onClose,
  onConfirm,
  submitting,
  error,
}) {
  const [selectedPlayerIds, setSelectedPlayerIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Filter to only show players who are available or haven't responded
  // (matches mobile app behavior)
  const eligiblePlayers = useMemo(() => {
    if (!availabilityRequest?.players) return [];
    return availabilityRequest.players.filter(
      (p) => p.isAvailable || !p.respondedAt
    );
  }, [availabilityRequest]);

  const filteredPlayers = useMemo(() => {
    if (!searchTerm) return eligiblePlayers;
    const term = searchTerm.toLowerCase();
    return eligiblePlayers.filter((p) => {
      const name = `${p.player?.firstName || ""} ${p.player?.lastName || ""}`;
      return name.toLowerCase().includes(term);
    });
  }, [eligiblePlayers, searchTerm]);

  const togglePlayer = (playerId) => {
    setSelectedPlayerIds((prev) => {
      if (prev.includes(playerId)) {
        return prev.filter((id) => id !== playerId);
      }
      return [...prev, playerId];
    });
  };

  const toggleSelectAll = () => {
    const allFilteredIds = filteredPlayers.map((p) => p.id);
    const allSelected = allFilteredIds.every((id) =>
      selectedPlayerIds.includes(id)
    );

    if (allSelected) {
      setSelectedPlayerIds((prev) =>
        prev.filter((id) => !allFilteredIds.includes(id))
      );
    } else {
      setSelectedPlayerIds((prev) => {
        const combined = new Set([...prev, ...allFilteredIds]);
        return [...combined];
      });
    }
  };

  const allSelected =
    filteredPlayers.length > 0 &&
    filteredPlayers.every((p) => selectedPlayerIds.includes(p.id));

  const getResponseLabel = (player) => {
    if (player.respondedAt && player.isAvailable) return "Yes";
    if (!player.respondedAt) return "Not Responded";
    return "";
  };

  if (loading) {
    return (
      <StyledDialog open={true} fullWidth onClose={onClose}>
        <DialogTitle>Select players to include in event</DialogTitle>
        <DialogContent
          style={{ display: "flex", justifyContent: "center", padding: "40px 20px" }}
        >
          <CircularProgress />
        </DialogContent>
      </StyledDialog>
    );
  }

  return (
    <StyledDialog open={true} fullWidth onClose={onClose}>
      <DialogTitle>Select players to include in event</DialogTitle>
      <DialogContent>
        <SingleLineTextInput
          placeholder="Search players..."
          value={searchTerm}
          onChange={(value) => setSearchTerm(value)}
        />

        <HeaderRow>
          <FormControlLabel
            control={
              <Checkbox
                checked={allSelected}
                onChange={toggleSelectAll}
                color="primary"
              />
            }
            label="Select All"
          />
          <SelectedCount>
            {selectedPlayerIds.length === 1
              ? "1 player selected"
              : `${selectedPlayerIds.length} players selected`}
          </SelectedCount>
        </HeaderRow>

        <div>
          {filteredPlayers.map((availabilityRequestPlayer) => (
            <PlayerRow key={availabilityRequestPlayer.id}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedPlayerIds.includes(
                      availabilityRequestPlayer.id
                    )}
                    onChange={() => togglePlayer(availabilityRequestPlayer.id)}
                    color="primary"
                  />
                }
                label={`${availabilityRequestPlayer.player?.firstName || ""} ${
                  availabilityRequestPlayer.player?.lastName || ""
                } (${getResponseLabel(availabilityRequestPlayer)})`}
              />
            </PlayerRow>
          ))}

          {filteredPlayers.length === 0 && (
            <Typography
              variant="body2"
              style={{ padding: "20px 0", color: "#999" }}
            >
              No eligible players found.
            </Typography>
          )}
        </div>

        {error && <ErrorMessage message={error} />}
      </DialogContent>
      <DialogActions>
        <Button
          title="Cancel"
          onClick={onClose}
          style={{ backgroundColor: "rgb(243, 243, 243)", color: "#222" }}
        />
        <Button
          title="Convert"
          onClick={() => onConfirm(selectedPlayerIds)}
          submitting={submitting}
          disabled={selectedPlayerIds.length === 0}
        />
      </DialogActions>
    </StyledDialog>
  );
}

export default ConvertAvailabilityRequestDialog;
